interface WrappedFetchProps {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers?: HeadersInit;
  url: string;
  data?: any;
  useCookieAuth?: boolean;
}

/**
 * Performs an HTTP request with optional cookie-based authentication
 * @param props - Request configuration
 * @returns Promise resolving to the response data
 */
function performWrappedFetch({
  method,
  headers,
  url,
  data,
  useCookieAuth,
}: WrappedFetchProps): Promise<any> {
  const baseOptions: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
  };

  const fetchOptions: RequestInit = {
    ...baseOptions,
    ...(useCookieAuth && { credentials: 'include' }),
  };

  // Only include body for methods that support it (not GET)
  if (method !== 'GET' && data !== undefined) {
    fetchOptions.body = JSON.stringify(data);
  }

  return fetch(url, fetchOptions).then((response: Response) => {
    if (!response.ok) {
      return Promise.reject(response);
    }

    // Handle 204 No Content - no body to parse
    if (response.status === 204) {
      return null;
    }

    // Check if Content-Length is explicitly 0
    const contentLength = response.headers.get('content-length');
    if (contentLength === '0') {
      return null;
    }

    // Try to parse as JSON, but handle empty responses gracefully
    return response.text().then((text) => {
      if (!text || text.trim().length === 0) {
        return null;
      }
      try {
        return JSON.parse(text);
      } catch (e) {
        // If parsing fails, return the text as-is
        return text;
      }
    });
  });
}

export { performWrappedFetch, WrappedFetchProps };
